import React, { FC, ReactNode, useEffect, useRef, useState, PropsWithChildren } from 'react';

import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { theme, variant } from '@lucid/theme';
/* eslint-disable-next-line no-restricted-imports */
import { Link as GatsbyLink, GatsbyLinkProps as LinkGatsbyProps } from 'gatsby';
import gsap from 'gsap';

import { Component } from '../../types/types';
import Icon from '../icon/icon';
import { BaseLinkOptions } from './link';

export interface StyledGatsbyLinkProps
  extends BaseLinkOptions,
    Omit<LinkGatsbyProps<unknown>, 'dangerouslySetInnerHTML'> {
  /** The children to be rendered. */
  children?: ReactNode;
  /** The link used in the Link component. */
  to: string;
  /** The available variants for the Link component. */
  variant?: Component.Type['Link'];
}

const StyledGatsbyLinkBase = styled(GatsbyLink, {
  shouldForwardProp: isPropValid,
})<StyledGatsbyLinkProps>(
  variant({
    scale: 'links',
    variants: {
      defaultVariant: {},
    },
  }),
  ({ theme, underline, displayBlock, isInverse }) => [
    {
      label: 'gatsbyLink',
      display: 'inline-block',
      fontWeight: theme.fontWeights.strong,
      textDecoration: 'none',
      cursor: 'pointer',
    },
    isInverse && {
      color: `${theme.colors.white} !important`,
      '&:active, &:focus': {
        color: `${theme.colors.coolGrey[40]} !important`,
      },
      '&:hover': {
        color: `${theme.colors.coolGrey[40]} !important`,
      },
    },
    underline === 'always' && {
      textDecoration: 'underline',
    },
    underline === 'always' ||
      (underline === 'hover' && {
        '&:hover, &:active, &:focus': {
          textDecoration: 'underline',
        },
      }),
    displayBlock && {
      display: 'block',
    },
  ],
);

const StyledGatsbyLink: FC<PropsWithChildren<StyledGatsbyLinkProps>> = ({
  activeClassName,
  activeStyle,
  className,
  children,
  displayBlock,
  hasArrow,
  iconColor,
  iconHoverColor,
  onClick,
  partiallyActive,
  replace,
  state,
  to,
  ref,
  variant = 'primary',
  ...rest
}) => {
  const animationRef = useRef<HTMLAnchorElement & GatsbyLink<unknown>>(null);
  const q = gsap.utils.selector(animationRef);
  const [hasHover, setHasHover] = useState(false);

  useEffect(() => {
    if (hasArrow && animationRef.current && q) {
      hasHover
        ? gsap
            .timeline({ repeat: -1, repeatDelay: 0.7 })
            .to(q('span'), {
              x: '0.6em',
              duration: 0.5,
              ease: theme.beziers.slowStartEase,
              overwrite: true,
            })
            .to(q('span'), { x: '0em', duration: 0.5, ease: theme.beziers.fastEndEase })
        : gsap.to(q('span'), { x: '0em', duration: 0.5, ease: theme.beziers.fastEndEase, overwrite: true });
    }
  }, [hasHover, hasArrow]);

  return (
    <StyledGatsbyLinkBase
      {...rest}
      activeClassName={activeClassName}
      /* eslint-disable-next-line react/jsx-sort-props */
      activeStyle={activeStyle}
      /* eslint-disable-next-line react/jsx-sort-props */
      className={className}
      data-link-as-gatsby="true"
      displayBlock={displayBlock}
      hasArrow={hasArrow}
      onClick={onClick}
      onMouseEnter={() => setHasHover(true)}
      onMouseLeave={() => setHasHover(false)}
      partiallyActive={partiallyActive}
      replace={replace}
      state={state}
      to={to}
      variant={variant}
    >
      {hasArrow ? (
        <span ref={animationRef}>
          {children}
          <span
            css={{
              label: 'hasArrow',
              position: 'relative',
              fontSize: '0.75em',
              marginLeft: '0.5em',
              display: 'inline-block',
              color: 'inherit',
            }}
          >
            <Icon color={iconColor} hoverColor={iconHoverColor} type="ArrowRight" />
          </span>
        </span>
      ) : (
        <>{children}</>
      )}
    </StyledGatsbyLinkBase>
  );
};

export { StyledGatsbyLink };
export default StyledGatsbyLink;
